import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, Linkedin, Mail } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    navigation: [
      { name: 'Home', path: '/' },
      { name: 'Chi Siamo', path: '/about' },
      { name: 'Servizi', path: '/services' },
      { name: 'Immobiliare', path: '/immobiliare' },
      { name: 'Contatti', path: '/contact' },
    ],
    services: [
      { name: 'Consulenza Marketplace', path: '/services#marketplace' },
      { name: 'Consulenza Ecommerce', path: '/services#ecommerce' },
      { name: 'Digital Marketing', path: '/services#marketing' },
      { name: 'Consulenza Immobiliare', path: '/immobiliare' },
    ],
  };

  return (
    <footer className="bg-[#1A1D24] border-t border-[#2D3139]">
      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
            Parliamone
          </h2>
          <p className="text-white/60 text-lg max-w-xl mx-auto mb-10">
            Pronto a scalare il tuo business digitale? Discutiamo della tua strategia di crescita.
          </p>
          <Link
            to="/contact"
            data-testid="footer-cta-button"
            className="inline-flex items-center gap-2 bg-[#2DD4BF] text-white px-8 py-4 font-semibold rounded-sm hover:bg-[#14B8A6] transition-all hover:scale-105 shadow-lg"
          >
            Richiedi Consulenza
            <ArrowUpRight size={20} />
          </Link>
        </motion.div>

        {/* Footer Links Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 py-12 border-t border-white/10">
          {/* Brand Column */}
          <div className="md:col-span-2">
            <Link
              to="/"
              data-testid="footer-logo"
              className="inline-block mb-4"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_e560243f-9eed-4536-b73b-967f4dba2a94/artifacts/d2156fag_Carone%20Consulting%20Logo%20%283%29.png" 
                alt="Carone Consulting"
                className="h-12 w-auto brightness-0 invert"
              />
            </Link>
            <p className="text-white/50 text-sm leading-relaxed max-w-sm">
              Agenzia di consulenza digitale e crescita ecommerce che aiuta le aziende a scalare attraverso strategie data-driven ed esecuzione.
            </p>
            <div className="flex gap-4 mt-6">
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-linkedin-link"
                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:border-[#2DD4BF] hover:text-[#2DD4BF] transition-colors"
              >
                <Linkedin size={18} />
              </a>
              <a
                href="mailto:contact@caroneconsulting.com"
                data-testid="footer-email-link"
                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:border-[#2DD4BF] hover:text-[#2DD4BF] transition-colors"
              >
                <Mail size={18} />
              </a>
            </div>
          </div>

          {/* Navigation Links */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
              Navigazione
            </h4>
            <ul className="space-y-3">
              {footerLinks.navigation.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-white/50 hover:text-[#2DD4BF] transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
              Servizi
            </h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-service-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-white/50 hover:text-[#2DD4BF] transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm">
            © {currentYear} Carone Consulting. Tutti i diritti riservati.
          </p>
          <div className="flex gap-6">
            <span className="text-white/40 text-sm">Privacy Policy</span>
            <span className="text-white/40 text-sm">Termini di Servizio</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
